import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserData } from 'app/shared/service/UserService';
import { normalizeAppTitle } from './linkable-apps.service';

export type TrainingMaterialActionType = 'ADDITION' | 'DELETION';
export type TrainingMaterialAction = 'APPROVE' | 'REJECTED';

// Test-only fallback. Backend returns null email fields today; until that's
// wired up we substitute a known mailbox so the workflow doesn't fall over.
// Remove the fallback usages once requestedByEmailId / approverEmailId /
// emailId fields are populated by the API.
export const TM_FALLBACK_EMAIL = 'shivesh@zibal.in';

export function tmEmailOrFallback(email?: string | null): string {
  const trimmed = (email || '').trim();
  return trimmed ? trimmed : TM_FALLBACK_EMAIL;
}

export interface TrainingMaterialUser {
  userId: string;
  username: string;
  emailId?: string;
}

export interface PracticeSetExerciseRecord {
  practiceSetExerciseId: string;
  practiceSetExerciseName: string;
}

export interface LinkedAppRecord {
  linkedAppId: string;
  linkedAppName: string;
  practiceSetExercises: PracticeSetExerciseRecord[];
}

export interface TrainingMaterialRecord {
  id: string;
  status: string;
  requestedByUserName: string;
  approverUserName: string;
  requestedByEmailId?: string;
  approverEmailId?: string;
  fileName: string;
  type: TrainingMaterialActionType;
  createdDate: string;
  actionDate?: string;
  linkedApps?: LinkedAppRecord[];
}

export interface PagedResponse<T> {
  status: boolean;
  record: T[];
  length: number;
}

export interface UploadMetadata {
  approverUserId: string;
  topic: string;
}

export interface UploadRequest {
  fileMetadataRequest: UploadMetadata[];
  type: TrainingMaterialActionType;
}

export interface ActionItem {
  id: string;
  action: TrainingMaterialAction;
}

export interface DeletionRequestItem {
  id: string;
  approverUserId: string;
}

export interface LinkedAppPayload {
  linkedAppId: string;
  practiceSetExercises: { practiceSetExerciseId: string }[];
}

export interface LinkAppsRequest {
  linkedApps: LinkedAppPayload[];
}

export interface ActionResultItem {
  id: string;
  success: boolean;
  record?: TrainingMaterialRecord;
  error?: string;
}

export interface ListQuery {
  pageNo: number;
  pageSize: number;
  sortFields?: string;
  sortDirection?: string;
  searchField?: string;
}

export interface TrainingMaterialItem {
  id: string;
  name: string;
}

export interface TrainingMaterialLinkedAppDetail {
  appId: string;
  appName: string;
  items: TrainingMaterialItem[];
}

export interface TrainingMaterialRow {
  id: string;
  fileName: string;
  fileType: string;
  type: string;
  status: string;
  requestedDate: string;
  requestedBy: string;
  approver: string;
  requesterEmail: string;
  approverEmail: string;
  createdDate: string;
  actionDate: string;
  linkedApps: string[];
  linkedApp: string;
  // Pre-built map of {appId: [exerciseIds]} so the link dialog can open with
  // the row's existing selections without a round-trip back to the API.
  linkedAppSelections: { [appId: string]: string[] };
  // appId → display name; used by the link dialog's chip rendering.
  linkedAppTitles: { [appId: string]: string };
  // Structured per-app linked items (with names) — feeds the pending tab's
  // view-only link dialog and the email's "Practice sets" line so neither
  // has to re-resolve names from IDs.
  linkedAppItems: TrainingMaterialLinkedAppDetail[];
  practiceSet: string;
}

@Injectable({ providedIn: 'root' })
export class TrainingMaterialService {
  private readonly baseUrl = `${(UserData.backend_url || '').replace(/\/+$/, '')}/v1/training-material`;

  constructor(private http: HttpClient) { }

  getApproverUsers(q: ListQuery): Observable<PagedResponse<TrainingMaterialUser>> {
    return this.http.get<PagedResponse<TrainingMaterialUser>>(
      `${this.baseUrl}/users`,
      { params: this.toParams(q) }
    );
  }

  uploadTrainingMaterial(request: UploadRequest, files: File[]): Observable<any> {
    const form = new FormData();
    form.append(
      'request',
      new Blob([JSON.stringify(request)], { type: 'application/json' })
    );
    files.forEach(f => form.append('files', f, f.name));
    return this.http.post(this.baseUrl, form);
  }

  getPendingSent(q: ListQuery): Observable<PagedResponse<TrainingMaterialRecord>> {
    return this.http.get<PagedResponse<TrainingMaterialRecord>>(
      `${this.baseUrl}/pending/sent`,
      { params: this.toParams(q) }
    );
  }

  getPendingReceived(q: ListQuery): Observable<PagedResponse<TrainingMaterialRecord>> {
    return this.http.get<PagedResponse<TrainingMaterialRecord>>(
      `${this.baseUrl}/pending/received`,
      { params: this.toParams(q) }
    );
  }

  action(actions: ActionItem[]): Observable<ActionResultItem[]> {
    return this.http.post<ActionResultItem[]>(`${this.baseUrl}/action`, actions);
  }

  getApproved(q: ListQuery): Observable<PagedResponse<TrainingMaterialRecord>> {
    return this.http.get<PagedResponse<TrainingMaterialRecord>>(
      `${this.baseUrl}/approved`,
      { params: this.toParams(q) }
    );
  }

  getRejectedDeleted(q: ListQuery): Observable<PagedResponse<TrainingMaterialRecord>> {
    return this.http.get<PagedResponse<TrainingMaterialRecord>>(
      `${this.baseUrl}/rejected-deleted`,
      { params: this.toParams(q) }
    );
  }

  permanentlyDeleteRejected(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/rejected-deleted/${id}`);
  }

  // The deletion-request endpoint expects every item to carry type=DELETION;
  // call sites only ever submit deletions, so the type tag is enforced here
  // rather than at every caller.
  requestDeletion(items: DeletionRequestItem[]): Observable<any> {
    const payload = items.map(i => ({ ...i, type: 'DELETION' as TrainingMaterialActionType }));
    return this.http.post(`${this.baseUrl}/deletion-request`, payload);
  }

  deletionAction(actions: ActionItem[]): Observable<ActionResultItem[]> {
    return this.http.post<ActionResultItem[]>(`${this.baseUrl}/deletion-action`, actions);
  }

  linkApps(id: string, payload: LinkAppsRequest): Observable<any> {
    return this.http.put(`${this.baseUrl}/${id}/link`, payload);
  }

  viewFile(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/view`, { responseType: 'blob' });
  }

  deleteRequest(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  withdrawDeletionRequest(id: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/deletion-request-withdraw/${id}`, {});
  }

  mapRecordToRow(r: TrainingMaterialRecord): TrainingMaterialRow {
    const linkedAppNames: string[] = [];
    const linkedAppSelections: { [appId: string]: string[] } = {};
    const linkedAppTitles: { [appId: string]: string } = {};
    const linkedAppItems: TrainingMaterialLinkedAppDetail[] = [];
    const practiceSetNames: string[] = [];
    (r.linkedApps || []).forEach(la => {
      // Backend ships raw game names ("Vectoring Game", "Advanced Vectoring
      // Game", "Match Game"); normalise so the table cell, hover popup, and
      // link-dialog sidebar all show the same title and the icon map resolves.
      const display = normalizeAppTitle(la.linkedAppName);
      linkedAppNames.push(display);
      const exercises = la.practiceSetExercises || [];
      linkedAppSelections[la.linkedAppId] = exercises.map(p => p.practiceSetExerciseId);
      linkedAppTitles[la.linkedAppId] = display;
      const items: TrainingMaterialItem[] = exercises
        .map(p => ({ id: p.practiceSetExerciseId, name: p.practiceSetExerciseName || '' }))
        .filter(it => !!it.id);
      linkedAppItems.push({ appId: la.linkedAppId, appName: display, items });
      items.forEach(it => { if (it.name) practiceSetNames.push(it.name); });
    });
    return {
      id: r.id,
      fileName: r.fileName,
      fileType: this.extractExtension(r.fileName),
      type: this.humanizeType(r.type),
      status: r.status,
      requestedDate: this.formatDate(r.createdDate),
      requestedBy: r.requestedByUserName || '',
      approver: r.approverUserName || '',
      requesterEmail: tmEmailOrFallback(r.requestedByEmailId),
      approverEmail: tmEmailOrFallback(r.approverEmailId),
      createdDate: r.createdDate,
      actionDate: this.formatDate(r.actionDate || ''),
      linkedApps: linkedAppNames,
      linkedApp: linkedAppNames[0] || '',
      linkedAppSelections,
      linkedAppTitles,
      linkedAppItems,
      practiceSet: Array.from(new Set(practiceSetNames)).join(', ')
    };
  }

  // Maps a sortable column id (matColumnDef) to the field name the backend
  // expects in `sortField`. UI columns are display-oriented (`requestedDate`,
  // `requestedBy`); the API ships raw record fields (`createdDate`,
  // `requestedByUserName`). Unknown keys pass through so already-aligned
  // columns (`fileName`, `type`, `status`, `actionDate`) keep working.
  toApiSortField(uiField: string): string {
    if (!uiField) return '';
    const map: { [k: string]: string } = {
      requestedDate: 'createdDate',
      requestedBy: 'requestedByUserName',
      approver: 'approverUserName',
      approvedDate: 'actionDate',
      actionBy: 'approverUserName'
    };
    return map[uiField] || uiField;
  }

  extractHttpError(err: any, fallback: string): string {
    if (!err) return fallback;
    const body = err.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
      if (body.error && typeof body.error.message === 'string') return body.error.message;
    }
    if (typeof err.message === 'string' && err.message.trim()) return err.message;
    return fallback;
  }

  summarizeActionFailures(failures: ActionResultItem[]): string {
    if (!failures.length) return '';
    const unique = Array.from(
      new Set(failures.map(f => (f.error || '').trim()).filter(Boolean))
    );
    return unique.join(' | ');
  }

  getMimeType(fileName: string): string {
    const ext = (fileName?.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'csv': return 'text/csv';
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      default: return 'application/octet-stream';
    }
  }

  private extractExtension(fileName: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop()!.toUpperCase() : '';
  }

  private humanizeType(t: string): string {
    if (!t) return '';
    const lower = t.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  private formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private toParams(q: ListQuery): HttpParams {
    return new HttpParams()
      .set('pageNo', String(q.pageNo ?? 0))
      .set('pageSize', String(q.pageSize ?? 10))
      .set('sortField', q.sortFields ?? '')
      .set('sortDirection', q.sortDirection ?? '')
      .set('searchField', q.searchField ?? '');
  }
}
